<template>
    <v-container fluid>
        <ShareDialog
            ref="shareDialog"
            :is-open="shareDialogVisible"
            :is-loading="shareDialogSaving"
            :document="document"
            :current-user-id="currentUserId"
            @save="handleShareSave"
            @close="closeShareDialog"
        />
        <page-title>{{ pageTitle }}</page-title>
        <v-row>
            <v-col class="pb-0">
                <v-btn text color="primary" class="pa-0" @click="handleReturnToDocumentsList">
                    <v-icon color="primary">
                        mdi-arrow-left
                    </v-icon>
                    Powrót do listy
                </v-btn>
            </v-col>
        </v-row>
        <v-row tag="form">
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12">
                            <v-card :loading="isLoading">
                                <TabsNav v-model="tabs" :items="tabItems" />

                                <v-tabs-items v-model="tabs">
                                    <v-divider />
                                    <v-tab-item>
                                        <Details
                                            :document="document"
                                            :is-edit-action="currentActionType === ACTION_TYPES.EDIT"
                                            :validation="detailsTabValidation"
                                            :is-read-only="!canEditDetails"
                                            :can-edit-as-admin="canEditAsAdmin"
                                            @field-blur="handleFieldBlur"
                                        />
                                    </v-tab-item>
                                    <v-tab-item>
                                        <AttachmentsTab :document="document" :is-read-only="!canManageAttachments" />
                                    </v-tab-item>
                                    <v-tab-item>
                                        <CommentsTab :document="document" :can-manage-comments="canManageComments" />
                                    </v-tab-item>
                                </v-tabs-items>
                            </v-card>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="auto">
                            <v-btn color="success" :disabled="isSaveDisabled" @click="handleSaveDocument">
                                Zapisz
                            </v-btn>
                        </v-col>
                        <v-col v-if="canShareDocument" cols="auto">
                            <v-btn color="purple" dark :loading="shareDialogOpening" @click="openShareDialog">
                                Udostępnij
                            </v-btn>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
import { errorHandler } from '@/utils/errorHandler'
import initialDocumentData from '@/views/Documents/Document/helpers/initialDocumentData'
import documentValidations from './helpers/validation'
import { ACTION_TYPES, PAGE_TITLES, STATUSES, createTabItems } from '@/views/Documents/Document/helpers/constants'
import { getFieldErrors } from '@/views/Documents/Document/helpers/validationErrors'
import { handleApiError } from '@/utils/apiHelpers'
import { mapState } from 'vuex'
import { hasUserPermission } from '@/utils/permissionsHandler'
import { normalizeAttachment } from '@/views/Documents/Document/helpers/attachments'


export default {
    name: 'Document',
    components: {
        PageTitle: () => import('@/components/common/PageTitle'),
        TabsNav: () => import('./Tabs/TabsNav.vue'),
        Details: () => import('./Tabs/DetailsTab/Index.vue'),
        AttachmentsTab: () => import('./Tabs/AttachmentsTab/Index.vue'),
        CommentsTab: () => import('./Tabs/CommentsTab/Index.vue'),
        ShareDialog: () => import('./ShareDialog.vue'),
    },
    beforeRouteEnter(to, from, next) {
        const id = to.params.id

        next(async vm => {
            vm.pageTitle = id ? PAGE_TITLES.EDIT : PAGE_TITLES.CREATE
            vm.currentActionType = id ? ACTION_TYPES.EDIT : ACTION_TYPES.CREATE

            try {
                vm.isDocumentLoading = true

                await Promise.all([
                    vm.$store.dispatch('documents/getProductionDepartmentsForDocuments'),
                    vm.$store.dispatch('documents/getTypes')
                ])

                if (id) {
                    await vm.loadDocument(id)
                }
            } catch (error) {
                handleApiError(error)
            } finally {
                vm.isDocumentLoading = false
            }
        })
    },

    data() {
        return {
            ACTION_TYPES,
            PAGE_TITLES,
            STATUSES,
            isDocumentLoading: false,
            pageTitle: '',
            currentActionType: '',
            tabItems: createTabItems(),
            tabs: 0,
            document: initialDocumentData(),
            shareDialogVisible: false,
            shareDialogSaving: false,
            shareDialogOpening: false
        }
    },
    validations: documentValidations,
    computed: {
        ...mapState('documents', [
            'productionDepartmentsLoading',
            'typesLoading'
        ]),
        currentUserId() {
            return this.$store.state.user.userId
        },
        userPermissions() {
            return this.$store.state.user.permissions
        },
        hasAdminPermission() {
            return hasUserPermission('docs_order_admin_update')
        },
        hasUpdatePermission() {
            return hasUserPermission('docs_order_update')
        },
        isNewDocument() {
            return !this.document.id
        },
        isDocumentClosed() {
            return this.document.statusId === STATUSES.DONE
        },
        isCreator() {
            if (this.isNewDocument) return true

            const creatorId = this.document.createdBy?.id

            if (!creatorId || !this.currentUserId) return false

            return creatorId === this.currentUserId
        },
        creatorPermissionsActive() {
            if (this.isNewDocument) return true

            return this.hasUpdatePermission && this.isCreator && !this.isDocumentClosed
        },
        canEditAsAdmin() {
            return this.hasAdminPermission
        },
        canSaveDocument() {
            if (this.isDocumentClosed) {
                return this.canEditAsAdmin
            }

            return this.canEditAsAdmin || this.creatorPermissionsActive || (this.isSharedUser && this.hasUpdatePermission)
        },
        canEditDetails() {
            return this.canSaveDocument
        },
        canManageAttachments() {
            return this.canEditAsAdmin || this.creatorPermissionsActive || (this.isSharedUser && this.hasUpdatePermission && !this.isDocumentClosed)
        },
        canManageComments() {
            return this.canEditAsAdmin || this.creatorPermissionsActive || (this.isSharedUser && this.hasUpdatePermission && !this.isDocumentClosed)
        },
        isLoading() {
            return this.productionDepartmentsLoading || this.typesLoading || this.isDocumentLoading
        },
        detailsTabValidation() {
            const documentValidation = this.$v?.document || {}

            const mapEntry = (fieldNode, ruleMessages, { skipWhen } = {}) => {
                if (typeof skipWhen === 'function' && skipWhen()) {
                    return {
                        error: false,
                        messages: []
                    }
                }

                return {
                    error: Boolean(fieldNode?.$error),
                    messages: getFieldErrors(fieldNode, ruleMessages)
                }
            }

            return {
                productionDepartmentId: mapEntry(
                    documentValidation.productionDepartmentId, {
                        required: 'Dział jest wymagany'
                    }
                ),
                typeId: mapEntry(
                    documentValidation.typeId, {
                        required: 'Rodzaj dokumentu jest wymagany'
                    }
                ),
                proposedDate: mapEntry(
                    documentValidation.proposedDate, {
                        required: 'Data jest wymagana'
                    }
                )
            }
        },
        isSaveDisabled() {
            return this.isLoading || !this.canSaveDocument
        },
        canShareDocument() {
            if (!this.document.id) return false

            return this.canEditAsAdmin || ((this.isCreator || this.isSharedUser) && !this.isDocumentClosed)
        },
        isSharedUser() {
            if (!this.document.id || !this.currentUserId) return false

            const sharedUserIds = (this.document.usersWithAccess || []).map(u => u.id)

            return sharedUserIds.includes(this.currentUserId)
        }
    },
    methods: {
        async loadDocument(documentId) {
            try {
                const { data } = await this.$store.dispatch('documents/getDocument', documentId)
                const mappedDocument = this.mapDocumentResponseToState(data)

                this.document = {
                    ...mappedDocument
                }

                if (data.number) {
                    this.pageTitle = `${this.PAGE_TITLES.EDIT} - ${data.number}`
                }
            } catch (error) {
                handleApiError(error)
            }
        },
        mapDocumentResponseToState(documentData) {
            if (!documentData || typeof documentData !== 'object') {
                return initialDocumentData()
            }

            return {
                id: documentData.id,
                status: documentData.status,
                statusId: documentData.statusId,
                proposedDate: documentData.proposedDate,
                productionDepartmentId: documentData.productionDepartmentId,
                typeId: documentData.typeId,
                createdBy: documentData.createdBy,
                attachments: Array.isArray(documentData.attachments)
                    ? documentData.attachments.map(normalizeAttachment).filter(Boolean)
                    : [],
                comments: [],
                usersWithAccess: Array.isArray(documentData.usersWithAccess)
                    ? documentData.usersWithAccess
                    : []
            }
        },
        buildDocumentPayload() {
            return {
                id: this.document.id,
                statusId: this.document.statusId,
                proposedDate: this.document.proposedDate,
                productionDepartmentId: this.document.productionDepartmentId,
                typeId: this.document.typeId,
                usersWithAccessIds: this.document.usersWithAccess.map(u => u.id)
            }
        },
        handleReturnToDocumentsList() {
            this.$router.push({ name: 'documentsDocuments' })
        },
        handleFieldBlur(field) {
            const handlers = {
                productionDepartmentId: () => this.$v.document.productionDepartmentId.$touch(),
                typeId: () => this.$v.document.typeId.$touch(),
                proposedDate: () => this.$v.document.proposedDate.$touch()
            }

            const handler = handlers[field]

            if (handler) handler()
        },
        async handleSaveDocument() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                await this.$store.dispatch('showNotification', { type: 'error', message: 'Formularz zawiera błędy.' })

                return
            }

            try {
                const payload = this.buildDocumentPayload()
                let documentId = this.document.id

                if (this.currentActionType === ACTION_TYPES.CREATE) {
                    const response = await this.$store.dispatch('documents/createDocument', payload)

                    documentId = response.data.id
                } else {
                    await this.$store.dispatch('documents/updateDocument', {
                        ...payload,
                        id: documentId
                    })
                }

                if (this.canManageComments && this.currentActionType === ACTION_TYPES.CREATE) {
                    await this.savePendingComments(documentId)
                }

                if (this.currentActionType === ACTION_TYPES.CREATE && this.document.attachments?.length > 0) {
                    await this.uploadAttachmentsInParallel(documentId)
                }

                await this.$router.push({ name: 'documentsDocuments' })
            } catch (error) {
                errorHandler(error)
            }
        },
        async uploadAttachmentsInParallel(documentId) {
            const uploadPromises = this.document.attachments.map(attachment => {
                const formData = new FormData()

                formData.append('file', attachment.file)

                return this.$store.dispatch('documents/createAttachment', {
                    docOrderId: documentId,
                    formData,
                    fileName: attachment.file.name
                })
            })

            await Promise.all(uploadPromises)
        },
        async savePendingComments(documentId) {
            const pendingComments = (this.document.comments || []).filter(comment => !comment.id)

            if (pendingComments.length === 0) return

            for (const pendingComment of pendingComments) {
                await this.$store.dispatch('documents/createDocumentComment', {
                    docOrderId: documentId,
                    content: pendingComment.content
                })
            }
        },
        async openShareDialog() {
            if (this.shareDialogOpening) return

            this.shareDialogSaving = false
            this.shareDialogOpening = true

            try {
                const shareDialog = this.$refs.shareDialog

                if (shareDialog && typeof shareDialog.prepareDialog === 'function') {
                    await shareDialog.prepareDialog()
                }

                this.shareDialogVisible = true
            } catch (error) {
                errorHandler(error)
            } finally {
                this.shareDialogOpening = false
            }
        },
        closeShareDialog() {
            if (this.shareDialogSaving) return
            this.shareDialogVisible = false
        },
        async handleShareSave(updatedUsers) {
            this.document.usersWithAccess = updatedUsers

            try {
                this.shareDialogSaving = true
                const payload = this.buildDocumentPayload()

                const response = await this.$store.dispatch('documents/updateDocument', {
                    ...payload,
                    id: this.document.id
                })

                const updatedFromApi = response?.data?.usersWithAccess

                if (Array.isArray(updatedFromApi)) {
                    this.document.usersWithAccess = updatedFromApi
                }

                this.shareDialogVisible = false
            } catch (error) {
                errorHandler(error)
            } finally {
                this.shareDialogSaving = false
            }
        }
    }
}
</script>
